import React, { useEffect, useState } from 'react';
import { Box, Typography, Slider, Button, IconButton } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import BottomSheetDialog from './mobile-bottom-sheet';

interface DescriptionMatchSettingsProps {
    open: boolean;
    sourceDescription?: string;
    initialStart?: number;
    initialLength?: number;
    onClose: () => void;
    onConfirm: (start: number, length: number) => void;
}

// Bottom-sheet phone equivalent of description-match-settings.tsx.
const DescriptionMatchSettingsMobile: React.FC<DescriptionMatchSettingsProps> = ({
    open,
    sourceDescription = '',
    initialStart = 0,
    initialLength = 3,
    onClose,
    onConfirm
}) => {
    const [start, setStart] = useState<number>(initialStart);
    const [length, setLength] = useState<number>(initialLength);

    const maxStart = sourceDescription.length > 0 ? sourceDescription.length - 1 : 0;
    const maxLength = sourceDescription.length - start;

    useEffect(() => {
        if (open) {
            setStart(initialStart);
            setLength(initialLength);
        }
    }, [open, initialStart, initialLength]);

    return (
        <BottomSheetDialog open={open} onClose={onClose} fitContent heightVh={85}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, borderBottom: '1px solid #e0e0e0' }}>
                <IconButton onClick={onClose}><ArrowLeft size={22} /></IconButton>
                <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>Description Match Settings</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Choose the character position to start matching from and the
                    minimum length required for a transaction to be considered a
                    match.
                </Typography>
                {sourceDescription && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption">Source description</Typography>
                        <Typography sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: 13 }}>
                            {sourceDescription}
                        </Typography>
                    </Box>
                )}

                <Typography gutterBottom>Start Position ({start})</Typography>
                <Slider
                    value={start}
                    onChange={(e, val) => {
                        const num = typeof val === 'number' ? val : val[0];
                        setStart(Math.min(Math.max(0, num), maxStart));
                        if (length > sourceDescription.length - num) {
                            setLength(sourceDescription.length - num);
                        }
                    }}
                    min={0}
                    max={maxStart}
                    valueLabelDisplay="auto"
                />

                <Typography gutterBottom>Match Length ({length})</Typography>
                <Slider
                    value={length}
                    onChange={(e, val) => {
                        const num = typeof val === 'number' ? val : val[0];
                        setLength(Math.min(Math.max(1, num), maxLength));
                    }}
                    min={1}
                    max={Math.max(1, maxLength)}
                    valueLabelDisplay="auto"
                />

                {sourceDescription && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="caption">Preview of matched segment</Typography>
                        <Typography sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', background: '#fff9c4', fontSize: 13 }}>
                            {sourceDescription.substring(start, start + length)}
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, p: 2, borderTop: '1px solid #e0e0e0' }}>
                <Button onClick={onClose} variant="outlined" fullWidth>Cancel</Button>
                <Button variant="contained" fullWidth onClick={() => onConfirm(start, length)}>Confirm</Button>
            </Box>
        </BottomSheetDialog>
    );
};

export default DescriptionMatchSettingsMobile;
